import Form from "../../Helpers/Form/Form";
import { Offcanvas } from "react-bootstrap";
import FormHeader from "../../Helpers/Form/FormParts/FormHeader";
import trans, { isRTL } from "../../../utils";
import TeamMemberFormBody from "./TeamMemberFormBody";
import {
  useCreateMemberMutation,
  useEditMemberMutation,
  useGetSpecificMemberQuery,
} from "../../../Redux/service/Teams/Teams";
import { Formik, Form as FormFormik } from "formik";
import Button from "../../Helpers/Button/Button";
import * as Yup from "yup";
import { useContext, useEffect } from "react";
import { popupcontext } from "../../../context/Popupscontext";
import { toast } from "react-toastify";

export default function FormTeamMember({
  teamMemberForm,
  setTeamMemberForm,
  isEditing,
}) {
  const { setEdited, handleClose } = useContext(popupcontext);
  const { refetch, data: editData } = useGetSpecificMemberQuery(
    teamMemberForm,
    {
      skip: !isEditing || !teamMemberForm,
    },
  );
  const [createMember] = useCreateMemberMutation();
  const [editMember] = useEditMemberMutation();

  useEffect(() => {
    if (isEditing && teamMemberForm) {
      refetch();
    }
  }, [isEditing, teamMemberForm, refetch]);

  let initialValues = {};
  if (isEditing && editData?.data) {
    const data = editData?.data;
    initialValues = {
      name: data?.name || "",
      email: data?.email || "",
      phone: data?.phone || "",
      systemRole: data?.systemRole || "",
    };
  } else {
    initialValues = {
      name: "",
      email: "",
      phone: "",
      systemRole: "",
    };
  }

  const validationSchema = Yup.object({
    name: Yup.string()
      .required(trans("Name is required"))
      .matches(/^[^\s]/, trans("cannot start with a space")),
    email: Yup.string()
      .email(trans("Invalid email address"))
      .required(trans("Email is required")),
    phone: Yup.string().required(trans("Phone number is required")),
    systemRole: Yup.string().required(trans("Role is required")),
  });

  const handleFormSubmit = async (values, setSubmitting, resetForm) => {
    setSubmitting(true);

    try {
      if (!isEditing && teamMemberForm) {
        await createMember(values).unwrap();
        toast.success(trans("Team member created successfully"), {
          autoClose: 800,
          rtl: localStorage.getItem("lang") === "ar" ? true : false,
        });
      } else {
        await editMember({
          id: teamMemberForm,
          val: values,
        }).unwrap();
        toast.success(trans("Team member edited successfully"), {
          autoClose: 800,
          rtl: localStorage.getItem("lang") === "ar" ? true : false,
        });
      }

      resetForm();
      setTeamMemberForm(false);
      setEdited(true);
      handleClose("form");
    } catch (err) {
      let errorMsg = trans(err?.data?.message ?? "Something went wrong");
      toast.error(errorMsg, {
        autoClose: 800,
        rtl: localStorage.getItem("lang") === "ar" ? true : false,
      });
    } finally {
      setSubmitting(false);
      handleClose("form");
    }
  };

  return (
    <>
      <Form show={teamMemberForm}>
        <Offcanvas.Header>
          <FormHeader
            title={
              !isEditing ? trans("Add Team Member") : trans("Edit Team Member")
            }
            other={false}
            onClose={() => {
              setTeamMemberForm(false);
              handleClose("form");
            }}
          />
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            onSubmit={(values, { setSubmitting, resetForm }) =>
              handleFormSubmit(values, setSubmitting, resetForm)
            }
            validationSchema={validationSchema}
          >
            {({ handleSubmit, isSubmitting }) => (
              <FormFormik onSubmit={handleSubmit} dir={!isRTL ? "ltr" : "rtl"}>
                <TeamMemberFormBody isEditing={isEditing} />
                <div className="offcanvas-footer m-0">
                  <div className="offcanvas-footer-body gap-8">
                    <Button
                      type={"primary"}
                      htmlType="submit"
                      loading={isSubmitting}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? trans("Saving...") : trans("Save")}
                    </Button>
                    <Button
                      type={"default"}
                      htmlType="button"
                      disabled={isSubmitting}
                      onClick={() => {
                        setTeamMemberForm(false);
                        handleClose("form");
                      }}
                    >
                      {trans("Cancel")}
                    </Button>
                  </div>
                </div>
              </FormFormik>
            )}
          </Formik>
        </Offcanvas.Body>
      </Form>
    </>
  );
}
